import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@shared/ui';
import React from 'react';
import type { FeedbackType } from '../api/artifactFeedbackApi';

const MAX_FEEDBACK_LENGTH = 1000;

export interface FeedbackModalProps {
  open: boolean;
  feedbackType: FeedbackType | null;
  onClose: () => void;
  onSubmit: (feedbackText: string) => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  feedbackType,
  onClose,
  onSubmit,
  isSubmitting = false,
  error = null,
}) => {
  const [feedbackText, setFeedbackText] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setFeedbackText('');
    }
  }, [open]);

  const handleSubmit = () => {
    onSubmit(feedbackText);
  };

  const placeholder =
    feedbackType === 'positive'
      ? 'What works well?'
      : feedbackType === 'negative'
        ? 'What could be improved or should be fixed?'
        : '';

  const helperText =
    'Your feedback will be shared with the team and influence how we build Moments. Thank you!';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '480px',
          maxWidth: '480px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '24px',
          lineHeight: 1.334,
          padding: '40px 40px 16px 40px',
        }}
      >
        Feedback
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '16px 40px 40px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          placeholder={placeholder}
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          disabled={isSubmitting}
          error={!!error}
          inputProps={{ maxLength: MAX_FEEDBACK_LENGTH }}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '12px',
            },
            '& textarea': {
              resize: 'none',
            },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'right', mt: '-8px' }}
        >
          {feedbackText.length}/{MAX_FEEDBACK_LENGTH}
        </Typography>
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
            {error}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            fontSize: '14px',
            lineHeight: 1.43,
            letterSpacing: '0.17px',
            fontWeight: 500,
          }}
        >
          {helperText}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px 40px',
          gap: '8px',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          color="gray"
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            padding: '6px 16px',
            fontSize: '13px',
            fontWeight: 700,
            lineHeight: '24px',
            letterSpacing: '0.4px',
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            padding: '6px 16px',
            fontSize: '13px',
            fontWeight: 700,
            lineHeight: '24px',
            letterSpacing: '0.4px',
            textTransform: 'none',
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
